import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { IconButton, Stack, styled, Menu, MenuItem, Avatar, Switch, FormControlLabel } from "@mui/material";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import { useNavigate } from "react-router-dom";
import WorkRoundedIcon from "@mui/icons-material/WorkRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import InviteModal from "../Pages/InviteModal";
import axios from "../axios";
import { logout } from "../store/actions/authActions";
import { connect } from "react-redux";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import CloseIcon from "@mui/icons-material/Close";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import { useThemeContext } from "../context/ThemeContext";
import { getUserInfo, isImpersonating, clearImpersonation } from "../utils/authStorage";

  const url = process.env.REACT_APP_BE_URL || '';

const RootSm = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("sm")]: {
    display: "none",
  },
}));

function ClippedDrawer(props) {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useThemeContext();

  const userInfo = getUserInfo();
  const impersonating = isImpersonating();

  const token = userInfo?.token;
  const id = userInfo?._id;

  const userName =
    userInfo?.name ||
    userInfo?.fullName ||
    (userInfo?.firstName || userInfo?.lastName
      ? `${userInfo?.firstName || ""} ${userInfo?.lastName || ""}`.trim()
      : null) ||
    userInfo?.email ||
    userInfo?.username ||
    userInfo?.userName ||
    "Admin User";

  const userType = (
    (userInfo && (userInfo.userType || userInfo.role)) || ""
  ).toString().toLowerCase();

  let roleLabel = "Super Admin";
  if (userType.includes("cashier")) roleLabel = "Cashier";
  else if (userType.includes("technician")) roleLabel = "Technician";
  else if (userType.includes("admin") || userType.includes("super"))
    roleLabel = "Super Admin";
  else if (userType) roleLabel = userType.charAt(0).toUpperCase() + userType.slice(1);

  // Prepare mobile menu items with role-based filtering (match Sidebar behavior)
  const allMenuItems = [
    { label: "Profile", path: `/profile/${id}`, icon: <AccountCircleRoundedIcon /> },
    { label: "Dashboard", path: `/profile/${id}/dashboard`, icon: <DashboardRoundedIcon /> },
    { label: "Jobs", path: "/jobs", icon: <DescriptionOutlinedIcon /> },
    { label: "Users", path: "/users", icon: <PeopleOutlineIcon /> },
    { label: "Suppliers", path: "/projects", icon: <LocalShippingRoundedIcon /> },
    { label: "Stock", path: "/jobsearch", icon: <ViewInArIcon /> },
    { label: "Invoice", path: "/invoices", icon: <ReceiptLongIcon /> },
  ];

  let allowedLabels = null;
  if (userType === "cashier") {
    allowedLabels = ["Profile", "Dashboard", "Jobs", "Stock", "Suppliers", "Invoice"];
  } else if (userType === "technician") {
    allowedLabels = ["Profile", "Jobs"];
  }

  const mobileMenuItems = allowedLabels
    ? allMenuItems.filter((m) => allowedLabels.includes(m.label))
    : allMenuItems;

  const [state, setState] = React.useState(false);
  const [openModel, setOpenModel] = React.useState(false);
  const [profilePic, setProfilePic] = React.useState(null);

  // Fetch user profile picture on mount
  React.useEffect(() => {
    const fetchProfilePic = async () => {
      try {
        if (id && token) {
          const config = {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          };
          const response = await axios.get(`${url}/user/${id}`, config);
          setProfilePic(response.data?.profilePic || response.data?.profilePic?.url || null);
        }
      } catch (error) {
        console.log("Error fetching profile picture:", error);
      }
    };
    fetchProfilePic();
  }, [id, token]);

  const handleCloseModel = () => setOpenModel(false);

  const logoutFunc = async () => {
    if (impersonating) {
      clearImpersonation();
      try {
        window.close();
      } catch (e) {
        // ignore
      }
      navigate("/signin");
      return;
    }
    try {
      const res = await axios.put('/user/logout', { id });
      if (res && (res.status === 200 || res.status === 204)) {
        props.logout(id);
        navigate("/signin");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const exitImpersonation = () => {
    clearImpersonation();
    try {
      window.close();
    } catch (e) {
      // ignore
    }
    navigate("/signin");
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setState(open);
  };

  const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState(null);
  const handleOpenMobileMenu = (event) => setMobileMenuAnchor(event.currentTarget);
  const handleCloseMobileMenu = () => setMobileMenuAnchor(null);

  const [desktopMenuAnchor, setDesktopMenuAnchor] = React.useState(null);
  const handleOpenDesktopMenu = (event) => setDesktopMenuAnchor(event.currentTarget);
  const handleCloseDesktopMenu = () => setDesktopMenuAnchor(null);

  // const drawer = () => (
  //   <Drawer
  //     open={false}
  //     variant="permanent"
  //     sx={{
  //       width: 80,
  //       [`& .MuiDrawer-paper`]: {
  //         width: 80,
  //         background: "#0066B2",
  //         display: "flex",
  //         alignItems: "center",
  //       },
  //     }}
  //   >
  //     <Toolbar />
  //     <List>
  //       {[
  //         {
  //           label: "Profile",
  //           icon: <AccountCircleRoundedIcon />,
  //           path: `/profile/${id}`,
  //           key: "profile",
  //         },
  //         {
  //           label: "Suppliers",
  //           icon: <AssignmentRoundedIcon />,
  //           path: "/projects",
  //           key: "projects",
  //         },
  //         {
  //           label: "Stock",
  //           icon: <WorkRoundedIcon />,
  //           path: "/jobsearch",
  //           key: "jobsearch",
  //         },
  //       ].map((item) => (
  //         <React.Fragment key={item.key}>
  //           <ListItem
  //             disablePadding
  //             sx={{
  //               width: 70,
  //               background:
  //                 location === item.key ? "#ffffff" : "transparent",
  //             }}
  //           >
  //             <ListItemButton
  //               sx={{ flexDirection: "column" }}
  //               onClick={() => navigate(item.path)}
  //             >
  //               <ListItemIcon sx={{ justifyContent: "center" }}>
  //                 {React.cloneElement(item.icon, {
  //                   sx: {
  //                     fontSize: 30,
  //                     color:
  //                       location === item.key ? "#000000" : "#ffffff",
  //                   },
  //                 })}
  //               </ListItemIcon>
  //               <ListItemText
  //                 primary={
  //                   <Typography
  //                     sx={{
  //                       fontSize: "0.7rem",
  //                       color:
  //                         location === item.key ? "#000000" : "#ffffff",
  //                     }}
  //                   >
  //                     {item.label}
  //                   </Typography>
  //                 }
  //               />
  //             </ListItemButton>
  //           </ListItem>
  //           <Divider sx={{ background: "#ffffff" }} />
  //         </React.Fragment>
  //       ))}
  //     </List>
  //   </Drawer>
  // );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* =================== APP BAR =================== */}
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: isDarkMode ? "#1e293b" : "#ffffff",
          boxShadow: isDarkMode ? "0 1px 6px rgba(0,0,0,0.3)" : "0 1px 6px rgba(16,24,40,0.06)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ px: 3, minHeight: 72 }}>
          {/* MOBILE MENU */}
          {!props.hideMenu && (
            <RootSm>
              <IconButton onClick={toggleDrawer(true)}>
                <MenuIcon />
              </IconButton>

              <Drawer
                open={state}
                onClose={toggleDrawer(false)}
                sx={{
                  [`& .MuiDrawer-paper`]: {
                    width: 240,
                    boxSizing: "border-box",
                    backgroundColor: isDarkMode ? "#0f172a" : "#ffffff",
                  },
                }}
              >
                <Toolbar />
                <Box px={2} pt={2}>
                  <List sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {mobileMenuItems.map((item) => {
                    const isActive = window.location.pathname === item.path || (item.label === "Profile" && window.location.pathname.startsWith("/profile"));
                    return (
                      <ListItem key={item.label} disablePadding>
                        <ListItemButton
                          onClick={() => navigate(item.path)}
                          sx={{
                            borderRadius: "12px",
                            px: 2,
                            py: 1.25,
                            backgroundColor: isActive ? (isDarkMode ? "#1e3a8a" : "#eff6ff") : (isDarkMode ? "#1e293b" : "transparent"),
                            "&:hover": { backgroundColor: isDarkMode ? "#334155" : "#f3f4f6" },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36, color: isActive ? "#2563eb" : (isDarkMode ? "#f1f5f9" : "#111827") }}>
                            {item.icon}
                          </ListItemIcon>

                          <ListItemText
                            primary={
                              <Typography sx={{ fontFamily: "Manrope, sans-serif", fontSize: "0.95rem", fontWeight: isActive ? 600 : 500, color: isActive ? "#2563eb" : (isDarkMode ? "#f1f5f9" : "#111827") }}>
                                {item.label}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>

                <Divider sx={{ mt: 2 }} />
              </Box>
            </Drawer>
          </RootSm>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              minWidth: 0,
            }}
            onClick={() => navigate(`/profile/${id}`)}
          >
            <Box
              sx={{
                width: { xs: 36, sm: 42 },
                height: { xs: 36, sm: 42 },
                borderRadius: 2,
                backgroundColor: "#7c3aed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <WorkRoundedIcon sx={{ color: "#ffffff", fontSize: { xs: 18, sm: 22 } }} />
            </Box>

            <Box sx={{ minWidth: 0, display: { xs: 'block', sm: 'block' } }}>
              <Typography
                sx={{
                  fontSize: { xs: '0.72rem', sm: 16 },
                  fontWeight: 700,
                  color: isDarkMode ? "#f1f5f9" : "#0f172a",
                  whiteSpace: 'nowrap',
                  overflow: 'visible',
                }}
              >
                Ranjith Electronics
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '0.6rem', sm: 12 },
                  color: isDarkMode ? "#cbd5e1" : "#64748b",
                  whiteSpace: 'nowrap',
                  overflow: 'visible',
                }}
              >
                TV Repair Management
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {impersonating && !props.hideUser && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mr: 1.5,
                px: 1,
                py: 0.5,
                borderRadius: '999px',
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(124,58,237,0.25), rgba(14,165,233,0.2))'
                  : 'linear-gradient(135deg, #ede9fe, #e0f2fe)',
                border: isDarkMode ? '1px solid rgba(167,139,250,0.45)' : '1px solid #c4b5fd',
                boxShadow: isDarkMode ? '0 0 0 1px rgba(124,58,237,0.15)' : '0 2px 8px rgba(124,58,237,0.12)',
              }}
            >
              <Chip
                icon={<SwitchAccountIcon sx={{ fontSize: '16px !important', color: isDarkMode ? '#c4b5fd !important' : '#6d28d9 !important' }} />}
                label={`Viewing as ${userInfo?.firstName || userName}`}
                size="small"
                sx={{
                  backgroundColor: 'transparent',
                  color: isDarkMode ? '#e9d5ff' : '#5b21b6',
                  fontWeight: 600,
                  fontSize: 12,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              <Typography
                sx={{
                  display: { xs: 'none', md: 'block' },
                  fontSize: 11,
                  fontWeight: 600,
                  color: isDarkMode ? '#93c5fd' : '#0369a1',
                  pr: 0.5,
                }}
              >
                {roleLabel}
              </Typography>
              <Tooltip title="Exit impersonation">
                <IconButton
                  size="small"
                  onClick={exitImpersonation}
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: isDarkMode ? 'rgba(239,68,68,0.2)' : '#fee2e2',
                    color: isDarkMode ? '#fca5a5' : '#dc2626',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(239,68,68,0.35)' : '#fecaca',
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Dark Mode Toggle */}
          {!props.hideUser && (
            <FormControlLabel
              control={
                <Switch
                  checked={isDarkMode}
                  onChange={toggleDarkMode}
                  sx={{
                    "& .MuiSwitch-switchBase": {
                      color: isDarkMode ? "#fbbf24" : "#0066B2",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#fbbf24",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#1f2937",
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: isDarkMode ? "#4b5563" : "#ccc",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: 14, fontWeight: 500, ml: 0.5, color: isDarkMode ? "#fbbf24" : "#0066B2" }}>
                  {isDarkMode ? "Dark" : "Light"}
                </Typography>
              }
              sx={{ mr: 1, mb: 0 }}
            />
          )}

          {/* Mobile profile icon: shows user details + logout on click */}
          {!props.hideUser && (
            <IconButton
              onClick={handleOpenMobileMenu}
              sx={{ display: { xs: "flex", sm: "none" } }}
              aria-controls={mobileMenuAnchor ? "mobile-user-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={mobileMenuAnchor ? "true" : undefined}
            >
              <AccountCircleRoundedIcon />
            </IconButton>
          )}

          {/* RIGHT USER */}
          {!props.hideUser && (
            <>
              <Stack 
                direction="row" 
                spacing={1} 
                alignItems="center" 
                sx={{ minWidth: 0, display: { xs: 'none', sm: 'flex' }, cursor: 'pointer' }}
                onClick={handleOpenDesktopMenu}
              >
                <Avatar
                  src={profilePic}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: "#7c3aed",
                    color: "#ffffff",
                    fontWeight: 700,
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  {!profilePic && userName.charAt(0).toUpperCase()}
                </Avatar>
              </Stack>
            </>
          )}
        </Toolbar>
      </AppBar>

      <InviteModal open={openModel} onClose={handleCloseModel} />

      {/* Mobile User Menu */}
      <Menu
        id="mobile-user-menu"
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleCloseMobileMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            handleCloseMobileMenu();
            navigate(`/profile/${id}`);
          }}
        >
          <ListItemIcon>
            <AccountCircleRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography sx={{ fontWeight: 600 }}>{userName}</Typography>
            }
            secondary={
              <Box sx={{ display: 'inline-block', px: 1.25, py: 0.35, borderRadius: 1, backgroundColor: isDarkMode ? '#312e81' : '#ede9fe', color: isDarkMode ? '#a5f3fc' : '#7c3aed', fontSize: { xs: 10, sm: 12 }, fontWeight: 600 }}>
                {roleLabel}
              </Box>
            }
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleCloseMobileMenu();
            logoutFunc();
          }}
        >
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Desktop User Menu */}
      <Menu
        id="desktop-user-menu"
        anchorEl={desktopMenuAnchor}
        open={Boolean(desktopMenuAnchor)}
        onClose={handleCloseDesktopMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            handleCloseDesktopMenu();
            navigate(`/profile/${id}`);
          }}
        >
          <ListItemIcon>
            <AccountCircleRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography sx={{ fontWeight: 600 }}>{userName}</Typography>
            }
            secondary={
              <Box sx={{ display: 'inline-block', px: 1.25, py: 0.35, borderRadius: 1, backgroundColor: isDarkMode ? '#312e81' : '#ede9fe', color: isDarkMode ? '#a5f3fc' : '#7c3aed', fontSize: { xs: 10, sm: 12 }, fontWeight: 600 }}>
                {roleLabel}
              </Box>
            }
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleCloseDesktopMenu();
            logoutFunc();
          }}
        >
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default connect(null, { logout })(React.memo(ClippedDrawer));
